import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Collapse, Box, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Grid, InputAdornment,
} from '@mui/material';
import { 
  KeyboardArrowDown, KeyboardArrowUp, History, Edit, Delete, 
  Search as SearchIcon, Clear as ClearIcon 
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../../api/axiosConfig';

const fetchVehicles = async () => {
  const { data } = await axios.get('/vehicles/with-devices');
  return data;
};

const deleteVehicle = async (id) => {
  const { data } = await axios.delete(`/vehicles/${id}`);
  return data;
};

const VehicleRow = ({ vehicle, onViewHistory, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);

  // Форматирование даты
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  // Проверяем наличие recentWorkOrders
  const hasWorkOrders = vehicle.recentWorkOrders && vehicle.recentWorkOrders.length > 0;

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{vehicle.licensePlate}</TableCell>
        <TableCell>{vehicle.vin || '-'}</TableCell>
        <TableCell>{vehicle.brand} {vehicle.model} ({vehicle.year})</TableCell>
        <TableCell>
          {vehicle.activeDevices?.map(device => (
            <Chip 
              key={device._id} 
              label={`${device.type}: ${device.serialNumber}`} 
              size="small" 
              color="primary" 
              sx={{ m: 0.5 }} 
            />
          ))}
          {(!vehicle.activeDevices || vehicle.activeDevices.length === 0) && 
            <Chip label="No devices" size="small" variant="outlined" />
          }
        </TableCell>
        <TableCell>
          <IconButton onClick={() => onViewHistory(vehicle._id)} title="History">
            <History />
          </IconButton>
          <IconButton onClick={() => onEdit(vehicle)} title="Edit">
            <Edit />
          </IconButton>
          <IconButton onClick={() => onDelete(vehicle)} title="Delete" color="error">
            <Delete />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom>
                Recent Work Orders
              </Typography>
              {hasWorkOrders ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Title</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Mechanic</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vehicle.recentWorkOrders.map((wo) => (
                      <TableRow key={wo._id}>
                        <TableCell>{wo.title}</TableCell>
                        <TableCell>{formatDate(wo.completedAt || wo.createdAt)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={wo.status} 
                            size="small" 
                            color={
                              wo.status === 'completed' ? 'success' : 
                              wo.status === 'in-progress' ? 'warning' : 
                              'default'
                            } 
                          />
                        </TableCell>
                        <TableCell>{wo.mechanicId?.fullName || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
                  No work orders found for this vehicle
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const VehicleTable = ({ onViewHistory, onEdit }) => {
  const [deleteDialog, setDeleteDialog] = useState({ open: false, vehicle: null });
  const [filters, setFilters] = useState({
    licensePlate: '',
    model: '',
  });
  const queryClient = useQueryClient();

  const { data: vehicles, isLoading, error } = useQuery({
    queryKey: ['vehicles'],
    queryFn: fetchVehicles,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteVehicle,
    onSuccess: () => {
      queryClient.invalidateQueries(['vehicles']);
      setDeleteDialog({ open: false, vehicle: null });
    },
    onError: (err) => {
      console.error('Delete error:', err);
      alert(err.response?.data?.message || 'Failed to delete vehicle');
      setDeleteDialog({ open: false, vehicle: null });
    },
  });

  const handleDelete = () => {
    if (deleteDialog.vehicle) {
      deleteMutation.mutate(deleteDialog.vehicle._id);
    }
  };

  const handleClearFilters = () => {
    setFilters({ licensePlate: '', model: '' });
  };

  // Фильтрация данных
  const filteredVehicles = vehicles?.filter(vehicle => {
    if (filters.licensePlate && !vehicle.licensePlate?.toLowerCase().includes(filters.licensePlate.toLowerCase())) {
      return false;
    }
    if (filters.model) {
      const fullModel = `${vehicle.brand} ${vehicle.model}`.toLowerCase();
      if (!fullModel.includes(filters.model.toLowerCase()) && 
          !vehicle.model?.toLowerCase().includes(filters.model.toLowerCase())) {
        return false;
      }
    }
    return true;
  });

  // Логирование для отладки
  console.log('Vehicles data:', vehicles);
  console.log('First vehicle recentWorkOrders:', vehicles?.[0]?.recentWorkOrders);

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading vehicles: {error.message}</Typography>;

  return (
    <>
      {/* Фильтры */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5} md={4}>
            <TextField
              fullWidth
              label="Search by License Plate"
              value={filters.licensePlate}
              onChange={(e) => setFilters({ ...filters, licensePlate: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filters.licensePlate && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFilters({ ...filters, licensePlate: '' })}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              placeholder="e.g., ABC-123"
            />
          </Grid>
          <Grid item xs={12} sm={5} md={4}>
            <TextField
              fullWidth
              label="Search by Model"
              value={filters.model}
              onChange={(e) => setFilters({ ...filters, model: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filters.model && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFilters({ ...filters, model: '' })}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              placeholder="e.g., Camry, Toyota Camry"
            />
          </Grid>
          <Grid item xs={12} sm={2} md={4}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClearFilters}
              disabled={!filters.licensePlate && !filters.model}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="textSecondary">
            Found {filteredVehicles?.length || 0} vehicle(s)
            {(filters.licensePlate || filters.model) && ` (filtered from ${vehicles?.length || 0} total)`}
          </Typography>
        </Box>
      </Paper>

      {/* Таблица */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'background.default' }}>
              <TableCell width={50} />
              <TableCell><strong>License Plate</strong></TableCell>
              <TableCell><strong>VIN</strong></TableCell>
              <TableCell><strong>Model</strong></TableCell>
              <TableCell><strong>Active Equipment</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredVehicles && filteredVehicles.length > 0 ? (
              filteredVehicles.map(vehicle => (
                <VehicleRow
                  key={vehicle._id}
                  vehicle={vehicle}
                  onViewHistory={onViewHistory}
                  onEdit={onEdit}
                  onDelete={(v) => setDeleteDialog({ open: true, vehicle: v })}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">
                    No vehicles found matching your criteria
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Диалог подтверждения удаления */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, vehicle: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete vehicle{' '}
            <strong>{deleteDialog.vehicle?.licensePlate}</strong>?
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            This action cannot be undone. The vehicle will be deactivated.
          </Typography>
          {deleteDialog.vehicle?.activeDevices?.length > 0 && (
            <Typography variant="caption" color="warning" sx={{ mt: 1, display: 'block' }}>
              Warning: This vehicle has {deleteDialog.vehicle.activeDevices.length} active device(s).
              Please remove them first.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, vehicle: null })}>Cancel</Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={deleteDialog.vehicle?.activeDevices?.length > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VehicleTable;